import { snippetSendFields } from '../../features/quick-replies/models/snippetUsageModel';
import { reportMessagingNetworkFailure } from "./connectionRecovery";
import axios from "axios";
import { API_DOMAIN } from "../constants";

import { reportQuarantineDenied } from "../../features/chat-security/models/quarantine";
import { runAssignment } from "./assignmentAttempt";
import { routingErrorPolicy } from '../../features/switchover/models/routingErrors';
import { assertMessagingWriteAllowed } from '../../features/switchover/models/messagingWriteGate';
import { requireClientReload } from '../../utils/clientReload';
import { registerSessionReset, sessionTicket, assertSessionCurrent } from '../../utils/sessionLifecycle';

const BASE_PATH = "/messaging-v1";

function authConfig(params, options = {}) {
  return {
    headers: { accessToken: localStorage.getItem("accessToken") },
    ...(params ? { params } : {}),
    ...options,
  };
}

function apiError(error, endpoint, silent = false) {
  const payload = error?.response?.data;
  const value = new Error(payload?.error?.message || payload?.message || error?.message || "Messaging data could not be retrieved.");
  value.backendMessage = payload?.error?.message || payload?.message || value.message;
  value.networkFailure = !error?.response && Boolean(error?.request || ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT'].includes(error?.code) || /network error|failed to fetch/i.test(error?.message || ''));
  if (!silent) reportMessagingNetworkFailure(value);
  value.status = error?.response?.status || null;
  value.code = payload?.error?.code || payload?.code || "MESSAGING_REQUEST_FAILED";
  value.details = payload?.error?.details || {};
  const policy = routingErrorPolicy(value.code, value.details);
  if (policy) { value.message = policy.message; value.retryAction = policy.retry; }
  if (policy?.retry === 'reload-page') requireClientReload(value.code === 'MESSAGING_SHARED_ONLY_DISABLED' ? 'shared-only' : 'version');
  if (value.code === 'ATTENDANCE_AGENT_UNAVAILABLE') value.message = "This person isn't available right now.";
  value.retryable = policy ? policy.retry === 'same-request' : Boolean(payload?.error?.retryable);
  value.requestId = payload?.meta?.request_id || null;
  value.endpoint = endpoint;
  return value;
}

function validateEnvelope(response, endpoint) {
  const body = response?.data;
  if (!body || body.success !== true || body.contract_version !== 1 || !body.data) {
    throw apiError({ response: { status: response?.status, data: body }, message: "Invalid Messaging V1 response." }, endpoint);
  }
  return body;
}

async function get(endpoint, params, options) {
  const session = localStorage.getItem("accessToken");
  const { silent = false, ...requestOptions } = options || {};
  try {
    return validateEnvelope(await axios.get(`${API_DOMAIN}${BASE_PATH}${endpoint}`, authConfig(params, requestOptions)), endpoint);
  } catch (error) {
    const failure = error?.endpoint ? error : apiError(error, endpoint, silent);
    reportQuarantineDenied(failure, endpoint, session);
    throw failure;
  }
}

async function mutate(method, endpoint, payload = {}, headers = {}) {
  if (endpoint === '/uploads' || /^\/conversations\/[^/]+\/(messages|template-messages|assignments|release)$/.test(endpoint)) assertMessagingWriteAllowed();
  const session = localStorage.getItem("accessToken");
  try {
    const response = await axios({
      method,
      url: `${API_DOMAIN}${BASE_PATH}${endpoint}`,
      ...authConfig(),
      headers: { ...authConfig().headers, ...headers },
      data: payload,
    });
    return validateEnvelope(response, endpoint);
  } catch (error) {
    const failure = error?.endpoint ? error : apiError(error, endpoint);
    reportQuarantineDenied(failure, endpoint, session);
    throw failure;
  }
}

const conversationPath = conversationId => `/conversations/${encodeURIComponent(conversationId)}`;
export const fetchSharedOnlySettings = async () => (await get('/settings/shared-only')).data;
export const saveSharedOnlySettings = async channels => (await mutate('patch', '/settings/shared-only', { channels })).data;
export const fetchWatiSweeperSettings = async () => (await get('/settings/wati-sweeper')).data;
export const saveWatiSweeperSettings = async payload => (await mutate('patch', '/settings/wati-sweeper', payload)).data;
export const fetchWatiBotCandidate = async () => (await get('/settings/wati-sweeper/bot-candidate')).data;
export const fetchSharedOnlySummary = async (params = { limit: 20 }) => (await get('/shared-only/summary', params)).data;

export async function releaseSharedOnlyConversation(conversationId, expectedVersion) {
  assertMessagingWriteAllowed();
  let actor;
  try { actor = JSON.parse(localStorage.getItem('auth'))?.id; } catch { /* Persistent coordinator fails closed. */ }
  return runAssignment(actor, conversationId, { expected_version: expectedVersion }, async (body, key, headers) => {
    const result = await mutate('post', `${conversationPath(conversationId)}/release`, body, { 'Idempotency-Key': key, ...headers });
    if (result.data.conversation_id !== conversationId || !Number.isSafeInteger(result.data.assignment_version) || typeof result.data.changed !== 'boolean' || typeof result.data.duplicate !== 'boolean' || !result.data.command_id) throw new Error('Release acceptance could not be confirmed. Retry the same request.');
    return result;
  }, 'release');
}

export const fetchChatQuarantineSettings = async () => (await get('/settings/quarantine')).data;
export const saveChatQuarantineSettings = async payload => (await mutate('put', '/settings/quarantine', payload)).data;
export const fetchChatSpamSignatures = async () => collection(await get('/settings/spam-signatures'), '/settings/spam-signatures').items;
export const createChatSpamSignature = async definition => (await mutate('post', '/settings/spam-signatures', definition)).data;
export const updateChatSpamSignature = async (id, definition) => (await mutate('patch', `/settings/spam-signatures/${encodeURIComponent(id)}`, definition)).data;
export const deleteChatSpamSignature = async (id, version) => (await mutate('delete', `/settings/spam-signatures/${encodeURIComponent(id)}`, { expected_version: version })).data;

export async function releaseCanonicalConversationQuarantine(conversationId, reason) {
  const text = reason.trim();
  if (!text || text.length > 1000) throw new Error('Enter a reason (up to 1,000 characters).');
  return (await mutate('post', `${conversationPath(conversationId)}/quarantine/release`, { reason: text })).data;
}

export async function releaseCanonicalAttachment(attachmentId, reason) {
  const text = reason.trim();
  if (!text || text.length > 1000) throw new Error('Enter a reason (up to 1,000 characters).');
  const data = (await mutate('post', `/attachments/${encodeURIComponent(attachmentId)}/release`, { reason: text })).data;
  if (data.attachment_id !== attachmentId || data.released !== true) throw new Error('Invalid file release response. Reload to check the file.');
  return data;
}

function collection(body, endpoint, paginated = false) {
  if (!Array.isArray(body.data?.items)) throw apiError({ message: "Invalid Messaging V1 collection." }, endpoint);
  if (paginated) {
    const page = body.data?.page;
    const cursorValid = page?.next_cursor === null || typeof page?.next_cursor === "string";
    if (!page || typeof page.has_more !== "boolean" || !cursorValid || (page.has_more && !page.next_cursor)) {
      throw apiError({ message: "Invalid Messaging V1 page." }, endpoint);
    }
  }
  return body.data;
}

export const messagingV1ErrorTelemetry = error => ({
  endpoint: error?.endpoint || null,
  status: error?.status || null,
  code: error?.code || "MESSAGING_REQUEST_FAILED",
  message: error?.message || "Messaging request failed.",
  details: error?.details || {},
  retryable: Boolean(error?.retryable),
  request_id: error?.requestId || null,
  occurred_at: new Date().toISOString(),
});

export async function fetchCanonicalChannelAccounts() {
  return collection(await get("/channel-accounts"), "/channel-accounts").items;
}

export async function fetchCanonicalConversationViews(channelAccountId = null) {
  const endpoint = "/conversation-views";
  const body = await get(endpoint, channelAccountId ? { channel_account_id: channelAccountId } : undefined);
  const data = collection(body, endpoint);
  return { items: data.items, default_view: data.default_view || data.items.find(view => view.is_default)?.key || null,
    views_policy_version: data.views_policy_version ?? null, channel_account_id: data.channel_account_id ?? channelAccountId };
}

// Effective chat members of one channel; no channel means customer_support.
const assigneeReads = new Map();
let assigneeRevision = 0;
registerSessionReset(() => { assigneeRevision++; assigneeReads.clear(); });
export function invalidateCanonicalAssignees() {
  assigneeRevision++;
  assigneeReads.forEach(entry => { entry.value = null; });
}
export function fetchCanonicalAssignees(channelAccountId = null) {
  const key = channelAccountId || '';
  const entry = assigneeReads.get(key) || {};
  assigneeReads.set(key, entry);
  const ticket = sessionTicket();
  if (entry.pending) {
    return entry.revision === assigneeRevision ? entry.pending : entry.pending.catch(() => {}).then(() => {
      assertSessionCurrent(ticket);
      return fetchCanonicalAssignees(channelAccountId);
    });
  }
  if (entry.value) return Promise.resolve(entry.value).then(rows => { assertSessionCurrent(ticket); return rows; });
  const revision = assigneeRevision;
  entry.revision = revision;
  entry.pending = get("/directory/assignees", channelAccountId ? { channel_account_id: channelAccountId } : undefined)
    .then(body => {
      assertSessionCurrent(ticket);
      const rows = collection(body, "/directory/assignees").items;
      if (revision === assigneeRevision) entry.value = rows;
      return rows;
    }).finally(() => { entry.pending = null; });
  return entry.pending;
}

export async function fetchChatMembers() {
  return collection(await get("/settings/chat-members"), "/settings/chat-members").items;
}

// body: { customer_member, customer_round_robin, supplier_member, expected_version }
export async function saveChatMember(userId, body) {
  const endpoint = `/settings/chat-members/${userId}`;
  return (await mutate("put", endpoint, body)).data;
}

export async function fetchCanonicalSnippets(channelAccountId = null) {
  return collection(await get("/snippets", channelAccountId ? { channel_account_id: channelAccountId } : undefined), "/snippets").items;
}

export async function fetchActionBindings(channelAccountId, action, admin = false) {
  const endpoint = admin ? "/settings/action-bindings" : "/action-bindings";
  return collection(await get(endpoint, { channel_account_id: channelAccountId, ...(action ? { action } : {}) }), endpoint).items;
}

export async function saveActionBinding(channelAccountId, action, snippetId, expectedVersion) {
  const endpoint = `/settings/action-bindings/${encodeURIComponent(action)}?channel_account_id=${encodeURIComponent(channelAccountId)}`;
  return collection(await mutate("put", endpoint, { snippet_id: snippetId, expected_version: expectedVersion }), endpoint).items;
}

export async function fetchMessagingChannelSettings(channelAccountId) {
  return (await get(`/settings/channels/${encodeURIComponent(channelAccountId)}`)).data;
}

export async function saveMessagingChannelSettings(channelAccountId, takeoverOnSend, expectedVersion) {
  return (await mutate("put", `/settings/channels/${encodeURIComponent(channelAccountId)}`, { takeover_on_send: takeoverOnSend, expected_version: expectedVersion })).data;
}

export async function fetchCanonicalSnippetAttachmentBlob(snippetId, attachmentId, channelAccountId, { review = false } = {}) {
  if (!/^snp_[a-f0-9]{32}$/.test(snippetId || "") || !/^att_[a-f0-9]{32}$/.test(attachmentId || "")
    || !/^cha_[a-f0-9]{32}$/.test(channelAccountId || "")) throw new Error("This quick reply's media has no valid channel or attachment. Nothing was sent.");
  const endpoint = `/snippets/${snippetId}/attachments/${attachmentId}/content`;
  try {
    const response = await axios.get(`${API_DOMAIN}${BASE_PATH}${endpoint}`, {
      ...authConfig(), responseType: "blob", params: { channel_account_id: channelAccountId, ...(review ? { review: 1 } : {}) },
    });
    return response.data;
  } catch (error) { throw apiError(error, endpoint); }
}

export async function fetchCanonicalTemplates(channelAccountId = null) {
  return collection(await get("/templates", channelAccountId ? { channel_account_id: channelAccountId } : undefined), "/templates").items;
}

export async function fetchCanonicalTemplateHeaderBlob(template) {
  const previewUrl = template?.header_media?.preview_url;
  const endpoint = `/templates/${encodeURIComponent(template?.id || "")}/header-media`;
  const url = new URL(previewUrl || "", API_DOMAIN);
  if (template?.header_media?.kind !== "image" || !previewUrl?.startsWith("/")
    || url.origin !== new URL(API_DOMAIN).origin
    || url.pathname !== `${BASE_PATH}${endpoint}`
    || !url.searchParams.get("channel_account_id")) {
    throw apiError({ message: "Invalid template header preview URL." }, endpoint);
  }
  try {
    const response = await axios.get(url.href, { ...authConfig(), responseType: "blob" });
    return response.data;
  } catch (error) {
    throw apiError(error, endpoint);
  }
}

export async function renameCanonicalSupplierParticipant(conversationId, displayName) {
  return (await mutate("patch", `${conversationPath(conversationId)}/participant`, { display_name: displayName.trim() })).data;
}

export async function removeCanonicalSupplierAssociation(conversationId, reason) {
  return (await mutate("delete", `${conversationPath(conversationId)}/supplier-association`, { reason: reason.trim() })).data;
}

export async function fetchCanonicalConversations(params = {}) {
  return collection(await get("/conversations", params), "/conversations", true);
}

const countReads = new Map();
registerSessionReset(() => countReads.clear());
export function fetchCanonicalConversationCount(params = {}) {
  const { limit, cursor, ...filters } = params;
  const key = JSON.stringify(Object.entries(filters).sort(([a], [b]) => a.localeCompare(b)));
  if (countReads.has(key)) return countReads.get(key);
  const ticket = sessionTicket();
  const request = get("/conversation-counts", filters).then(body => {
    assertSessionCurrent(ticket);
    return body.data;
  }).finally(() => { if (countReads.get(key) === request) countReads.delete(key); });
  countReads.set(key, request);
  return request;
}

export async function fetchCanonicalConversation(conversationId) {
  return (await get(conversationPath(conversationId))).data;
}

export async function fetchCanonicalMessages(conversationId, params = {}, options) {
  const endpoint = `${conversationPath(conversationId)}/messages`;
  return collection(await get(endpoint, params, options), endpoint, true);
}

export async function fetchMessagingRecoverySnapshot(conversationId) {
  return (await get(`${conversationPath(conversationId)}/recovery-snapshot`, { limit: 50 })).data;
}

export async function fetchCanonicalLinkPreview(url) {
  return (await get("/link-previews", { url })).data;
}

export async function fetchMessagingRecoveryEvents(conversationId, cursor) {
  return (await get(`${conversationPath(conversationId)}/events`, { cursor, limit: 50 })).data;
}

export async function fetchCanonicalAttachmentBlob(attachmentId, { withMetadata = false } = {}) {
  const endpoint = `/attachments/${encodeURIComponent(attachmentId)}/content`;
  try {
    const response = await axios.get(`${API_DOMAIN}${BASE_PATH}${endpoint}`, {
      ...authConfig(), responseType: "blob",
    });
    return withMetadata ? { blob: response.data, contentDisposition: response.headers?.["content-disposition"] } : response.data;
  } catch (error) {
    // Axios returns JSON errors as blobs too when responseType is blob.
    if (error.response?.data instanceof Blob && typeof error.response.data.text === "function") {
      try { error.response.data = JSON.parse(await error.response.data.text()); } catch { /* Keep the HTTP status if the error body isn't JSON. */ }
    }
    throw apiError(error, endpoint);
  }
}

export async function fetchCanonicalQueryHistory(conversationId, { cursor = null, limit = 10, signal } = {}) {
  const endpoint = `${conversationPath(conversationId)}/query-history`;
  return (await get(endpoint, { limit, ...(cursor ? { cursor } : {}) }, signal ? { signal } : undefined)).data;
}

export async function fetchCanonicalNotes(conversationId) {
  const endpoint = `${conversationPath(conversationId)}/notes`;
  return collection(await get(endpoint), endpoint).items;
}

export async function createCanonicalNote(conversationId, text) {
  return mutate("post", `${conversationPath(conversationId)}/notes`, { text });
}

export async function updateCanonicalNote(conversationId, noteId, text) {
  return mutate("patch", `${conversationPath(conversationId)}/notes/${encodeURIComponent(noteId)}`, { text });
}

export async function deleteCanonicalNote(conversationId, noteId) {
  return mutate("delete", `${conversationPath(conversationId)}/notes/${encodeURIComponent(noteId)}`);
}

export async function markCanonicalConversationRead(conversationId) {
  return mutate("post", `${conversationPath(conversationId)}/read`);
}

export async function markCanonicalConversationUnread(conversationId) {
  return mutate("post", `${conversationPath(conversationId)}/unread`, {});
}

export async function reactToCanonicalMessage(conversationId, messageId, emoji) {
  return mutate("post", `/messages/${encodeURIComponent(messageId)}/reactions`, { conversation_id: conversationId, emoji });
}

export async function setCanonicalConversationStar(conversationId, starred) {
  return mutate("post", `${conversationPath(conversationId)}/${starred ? "star" : "unstar"}`);
}

export async function setCanonicalConversationBlock(conversationId, blocked) {
  return mutate("post", `${conversationPath(conversationId)}/${blocked ? "block" : "unblock"}`);
}

export async function setCanonicalConversationLifecycle(conversationId, status) {
  const action = { Open: "reopen", Pending: "pending", Solved: "resolve" }[status];
  if (!action) throw new Error(`Unsupported conversation status: ${status}`);
  return mutate("post", `${conversationPath(conversationId)}/${action}`);
}

export async function assignCanonicalConversation(conversationId, assignedUserId, reason) {
  assertMessagingWriteAllowed();
  let actor;
  try { actor = JSON.parse(localStorage.getItem("auth"))?.id; } catch { /* Fail closed in the persistent command coordinator. */ }
  return runAssignment(actor, conversationId, {
    assigned_to_user_id: assignedUserId,
    ...(reason?.trim() ? { reason: reason.trim() } : {}),
  }, (body, key, headers) => {
    if (String(JSON.parse(localStorage.getItem("auth"))?.id) !== String(actor)) throw new Error("The signed-in user changed. Nothing was assigned.");
    return mutate("post", `${conversationPath(conversationId)}/assignments`, body, { "Idempotency-Key": key, ...headers });
  });
}

export async function sendCanonicalText(conversationId, { clientMessageId, text, replyToMessageId = null, ...snippetUse }) {
  return mutate("post", `${conversationPath(conversationId)}/messages`, {
    client_message_id: clientMessageId,
    kind: "text",
    content: { kind: "text", text },
    reply_to_message_id: replyToMessageId,
    ...snippetSendFields(snippetUse),
  });
}

export async function createCanonicalUpload({ kind, fileName, mimeType, dataBase64 }) {
  const body = await mutate("post", "/uploads", {
    kind, file_name: fileName, mime_type: mimeType, data_base64: dataBase64,
  });
  if (!body.data?.id) throw new Error("The upload did not return an ID.");
  return body.data;
}

export async function sendCanonicalMedia(conversationId, { clientMessageId, kind, uploadId, caption = "", replyToMessageId = null, ...snippetUse }) {
  return mutate("post", `${conversationPath(conversationId)}/messages`, {
    client_message_id: clientMessageId,
    kind,
    content: { kind, upload_id: uploadId, caption },
    reply_to_message_id: replyToMessageId,
    ...snippetSendFields(snippetUse),
  });
}

export async function sendCanonicalTemplate(conversationId, { clientMessageId, templateName, parameters }) {
  return mutate("post", `${conversationPath(conversationId)}/template-messages`, {
    client_message_id: clientMessageId,
    template_name: templateName,
    parameters,
  });
}
